import React, { createContext, useContext, useEffect, useState } from 'react';
import { tokenStore } from './tokenStore';
import { ParsedToken } from './jwt';

interface UserState {
  admin: boolean;
  products: string[];
  isGuest: boolean;
  accessToken: string;
  parsedToken?: ParsedToken;
}

const defaultState: UserState = {
  admin: false,
  products: [],
  isGuest: true,
  accessToken: '',
};

function deriveFromParsed(parsed: ParsedToken | null) {
  const user = parsed?.user;
  const policies = user?.policies ?? [];
  const role = user?.role ?? '';
  const isVlab = user?.is_vlab ?? false;
  const admin = policies.includes('vlab:iam:admin') || role === 'admin' || isVlab;
  const products = policies.map((p) => p.split(':')[1]).filter(Boolean);
  return { admin, products };
}

interface AuthContextValue {
  state: UserState;
}

const AuthContext = createContext<AuthContextValue>({ state: defaultState });

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<UserState>(() => {
    const token = tokenStore.get();
    const parsed = tokenStore.getParsed();
    const { admin, products } = deriveFromParsed(parsed);
    return {
      ...defaultState,
      accessToken: token ?? '',
      parsedToken: parsed ?? undefined,
      admin,
      products,
      isGuest: !token,
    };
  });

  useEffect(() => {
    const apply = (token: string | null) => {
      const parsed = tokenStore.getParsed();
      const { admin, products } = deriveFromParsed(parsed);
      setState({
        accessToken: token ?? '',
        parsedToken: parsed ?? undefined,
        admin,
        products,
        isGuest: !token,
      });
    };
    return tokenStore.subscribe(apply);
  }, []);

  return <AuthContext.Provider value={{ state }}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  return useContext(AuthContext);
}
